import { ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DashboardCard } from '../widgets/DashboardCard'
import { ProgressBar } from '../widgets/ProgressBar'
import { WidgetMenu, WidgetMenuItem } from '../widgets/WidgetMenu'
import { runQuery } from '../engine'
import { frameOf } from '../frames'
import { DashboardTile as Tile } from '../dashboards'
import { resolveThreshold, useKpis } from '../kpis'
import { opsToConfig } from '../pipeline-compile'
import { listSavedDashboards } from '../saved-dashboards'
import { getSavedView } from '../saved-views'
import { getTemplate } from '../templates'
import { QueryResult, ViewConfig } from '../view-config'
import { SourceBadge } from './LibraryTable'
import { DashboardTileChart } from './DashboardTileChart'
import { MetricView } from './MetricView'
import { RankedListView } from './RankedListView'
import { ReportChart, ReportChartType } from './ReportChart'
import { formatCell, ReportTable } from './ReportTable'
import { availableViews, resolveView, ReportView } from './ViewSwitcher'

/**
 * Kebab actions on a dashboard tile. Most are placeholder handlers for the
 * prototype (backend wired later); "Edit" opens the tile's underlying report in
 * the builder (edit mode). "Add to…" drills into the user's dashboards so a
 * widget can be dropped onto another dashboard.
 *
 * @param onEdit - open the underlying report in edit mode (see DashboardTileCard)
 */
function tileMenuItems(onEdit: () => void): WidgetMenuItem[] {
  const dashboards = listSavedDashboards()
  return [
    {
      label: 'Add to…',
      submenu:
        dashboards.length > 0
          ? dashboards.map((d) => ({ label: d.name }))
          : [{ label: 'No dashboards yet' }],
    },
    { label: 'Export as image' },
    { label: 'Pin to homepage' },
    { label: 'Edit', onSelect: onEdit },
  ]
}

/** Rows shown by a list tile — full data lives on the detail page. */
const LIST_TILE_ROWS = 5

/**
 * Compact preview of a list view as a ranked list (borrowing the country-list
 * row pattern from the maps widgets): each deal's company + owner/type, the
 * sort metric's value, and a magnitude bar relative to the top row. Prettier
 * and denser than a raw table for "top N" lists.
 */
function MiniRankedList({ config }: { config: ViewConfig }) {
  const frame = frameOf(config.frame)
  const result = useMemo(
    () => runQuery({ ...config, limit: LIST_TILE_ROWS }, frame),
    [config, frame]
  )
  if (result.kind !== 'list') return null
  // Primary/secondary labels are the report's own first two columns (so a
  // "my deals" list leads with the account, not the redundant owner name);
  // fall back to the frame defaults. Rank by the view's sort field (else the
  // frame's first numeric column).
  const [primary, secondary] = config.columns ?? frame.defaultColumns
  const valueField = config.sort?.by ?? frame.numFields[0] ?? frame.columns[0]
  const { rows } = result
  const max = Math.max(...rows.map((r) => Number(r[valueField]) || 0), 1)

  return (
    <div className="flex flex-col divide-y divide-line">
      {rows.map((row, i) => {
        const value = Number(row[valueField]) || 0
        return (
          <div key={i} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-fg">
                {String(row[primary] ?? '')}
              </p>
              {secondary && (
                <p className="truncate text-xs text-fg-secondary">
                  {String(row[secondary] ?? '')}
                </p>
              )}
            </div>
            <div className="flex w-28 shrink-0 flex-col items-end gap-1">
              <span className="text-sm font-semibold text-fg">
                {formatCell(valueField, value, frame)}
              </span>
              <ProgressBar value={(value / max) * 100} className="w-full" />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * The live renderable config for a tile. The tile stores a config snapshot from
 * when it was added; this resolves the report as it is *now* so an edit shows on
 * the tile — pipeline reports recompile their op-chain (like the detail page),
 * flat reports are a ViewConfig already. Falls back to the snapshot if the
 * report was deleted. Used for both the tile body and the builder's view picker
 * so they never disagree about the report's shape.
 */
export function resolveTileConfig(tile: Tile): ViewConfig {
  const saved = getSavedView(tile.config.id)
  if (saved?.pipeline)
    return opsToConfig(saved.pipeline, saved.pipelineFrame ?? 'deal')
  return saved ?? getTemplate(tile.config.id) ?? tile.config
}

/**
 * The view a tile shows: its saved per-tile override when that override is valid
 * for the report's shape, else the report's own default view — the same resolver
 * the detail page uses, so a tile reads as the report does (a report saved as a
 * line shows as a line on the dashboard, not silently demoted to a bar).
 *
 * @param tile - the dashboard tile (its `view` is the optional override)
 * @param config - the tile's report config
 * @param result - the computed result (drives which views are valid)
 * @returns the ReportView to render on the tile
 */
export function effectiveTileView(
  tile: Tile,
  config: ViewConfig,
  result: QueryResult
): ReportView {
  // Only honor the per-tile override while it still fits the report's shape; a
  // stale override (shape changed under it) falls through to the report's own
  // default view, same as the detail page.
  const valid = availableViews(config, result).some((v) => v.view === tile.view)
  return resolveView(config, result, valid ? (tile.view ?? null) : null)
}

/**
 * Renders a tile's body in a given view, reusing the report's own renderers so a
 * tile reads the same as the report detail page. bar/stacked keep the dashboard
 * tile chart treatment; the other encodings reuse ReportChart.
 */
function TileBody({
  config,
  result,
  view,
  listAsTable = false,
}: {
  config: ViewConfig
  result: QueryResult
  view: ReportView
  /**
   * Render a list tile as the full sortable ReportTable rather than the compact
   * MiniRankedList preview. Opt-in (a tile with an explicit `view: 'table'`
   * override) so other list tiles keep the denser ranked preview by default.
   */
  listAsTable?: boolean
}) {
  if (view === 'metric')
    return <MetricView config={config} result={result} fill />
  if (view === 'ranked')
    return <RankedListView config={config} result={result} />
  if (view === 'table')
    // A raw deal list reads better as the dense ranked preview by default; an
    // explicit table override shows the full sortable table. A grouped summary
    // always uses the real (few-row) summary table.
    return config.kind === 'list' ? (
      listAsTable ? (
        <ReportTable config={config} result={result} fill sortable />
      ) : (
        <MiniRankedList config={config} />
      )
    ) : (
      <ReportTable config={config} result={result} />
    )
  if (view === 'bar' || view === 'stacked-bar')
    return (
      <DashboardTileChart
        variant={view === 'stacked-bar' ? 'stacked' : 'bar'}
        config={config}
        result={result}
      />
    )
  // horizontal-bar / line / dot / grouped-bar — reuse the full chart renderer,
  // bare and filling the tile (fixed height would clip the axis in a short tile).
  return (
    <ReportChart
      bare
      fill
      chartType={view as ReportChartType}
      config={config}
      result={result}
    />
  )
}

/**
 * One dashboard tile: compact chart or mini-table, whole card clickable
 * through to the full report (knobs live there, not on the tile). The tile's
 * `view` override decides how the report is shown here without touching the
 * report's own saved view.
 */
export function DashboardTileCard({
  tile,
  linkTo,
  action,
}: {
  tile: Tile
  /**
   * Card link target. Defaults to the tile's report detail page; pass `null` to
   * disable navigation (e.g. on the dashboard builder canvas, where clicking a
   * tile shouldn't leave the page).
   */
  linkTo?: string | null
  /** Override the header action slot (defaults to the tile kebab menu). */
  action?: ReactNode
}) {
  const { config } = tile
  const navigate = useNavigate()
  const { kpis } = useKpis()
  // The tile's stored config is a snapshot from when it was added; resolve the
  // live report so an edit to the report (shape, name, description) shows on the
  // tile. Pipeline reports compile their op-chain the same way the detail page
  // does; flat reports are a ViewConfig already. Fall back to the snapshot if
  // the report was deleted.
  const saved = getSavedView(config.id)
  const live = saved ?? getTemplate(config.id)
  const liveConfig = resolveTileConfig(tile)
  const result = useMemo(
    () => runQuery(liveConfig, frameOf(liveConfig.frame)),
    [liveConfig]
  )
  const view = effectiveTileView(tile, liveConfig, result)
  // Resolve KPI-linked targets for display; the query above is threshold-free.
  const displayConfig = resolveThreshold(liveConfig, kpis)

  const href =
    linkTo === undefined ? `/reporting/${config.id}` : (linkTo ?? undefined)
  // A saved report is "Mine"; anything else is a shipped (Pre-built) report.
  const source = saved ? 'mine' : 'prebuilt'
  const actionNode =
    action !== undefined ? (
      action
    ) : (
      <div className="flex items-center gap-1.5">
        <SourceBadge source={source} iconOnly />
        <WidgetMenu
          items={tileMenuItems(() =>
            navigate(`/reporting/${config.id}?edit=1`)
          )}
        />
      </div>
    )

  // h-full fills the grid cell (react-grid-layout sizes the cell to the tile's
  // h×rowHeight); overflow-hidden clips any chart/table that exceeds it — the
  // full data lives on the report detail page the card links to.
  const tileName = live?.name ?? config.name
  const tileDescription = live?.description ?? config.description
  // Chart tiles center their title over the plot (a left title leaves the top
  // lopsided next to the y-axis); metric/ranked/table tiles keep it left so the
  // caption stays flush with their left-aligned bodies.
  const isChart = view !== 'metric' && view !== 'ranked' && view !== 'table'
  return (
    <DashboardCard
      href={href}
      titleAlign={isChart ? 'center' : 'left'}
      // Single-line + ellipsis: a long name shouldn't wrap and shove a metric's
      // number down. Description (else the full name) rides as a hover tooltip so
      // the truncated text is still recoverable.
      title={
        <span className="block truncate" title={tileDescription ?? tileName}>
          {tileName}
        </span>
      }
      action={actionNode}
      className="h-full overflow-hidden"
      bodyClassName="min-h-0 overflow-hidden"
    >
      <TileBody
        config={displayConfig}
        result={result}
        view={view}
        listAsTable={tile.view === 'table'}
      />
    </DashboardCard>
  )
}
